import './App.css'
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext'
import { MindfulnessProvider } from './contexts/MindfulnessContext'
import { TaskProvider } from './contexts/TaskContext'
import { MobileSidebarProvider } from './hooks/useMobileSidebar'
import { lazy, Suspense, useEffect } from 'react'
import Login from './components/Login'
import { ImageLightbox } from './components/ImageLightbox'
import { isAppleNativeRuntime, isNativePlatformRuntime } from './lib/runtimePlatform'

const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const WorkspaceAdminDashboard = lazy(() => import('./components/admin/WorkspaceAdminDashboard'));
const MainLayout = lazy(() => import('./components/MainLayout'));
const KindeCallback = lazy(() => import('./components/auth').then(module => ({ default: module.KindeCallback })));
const InvitationAcceptPage = lazy(() => import('./components/auth').then(module => ({ default: module.InvitationAcceptPage })));
const WorkspaceJoinPage = lazy(() => import('./components/auth').then(module => ({ default: module.WorkspaceJoinPage })));
const WorkspacePicker = lazy(() => import('./components/workspace').then(module => ({ default: module.WorkspacePicker })));
const WorkspaceCreate = lazy(() => import('./components/workspace').then(module => ({ default: module.WorkspaceCreate })));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const ShareReceiverPage = lazy(() => import('./components/ShareReceiverPage'));
const NativePlatformRuntime = lazy(() => import('./components/NativePlatformRuntime').then(module => ({ default: module.NativePlatformRuntime })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading } = useAuth();
  
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }
  
  return isAdmin ? <>{children}</> : <Navigate to="/" />;
}

function WorkspaceAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin: isSuperAdmin, loading: authLoading } = useAuth();
  const { isWorkspaceAdmin, isLoadingWorkspaces } = useWorkspace();
  
  if (authLoading || isLoadingWorkspaces) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Allow access if user is workspace admin OR super admin
  return (isWorkspaceAdmin || isSuperAdmin) ? <>{children}</> : <Navigate to="/" />;
}

function HomeRoute() {
  const { currentWorkspaceId, workspaces, isLoadingWorkspaces, workspaceError } = useWorkspace();
  const isAppleNativeApp = isAppleNativeRuntime();

  if (isLoadingWorkspaces) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (workspaceError || currentWorkspaceId) {
    return <MainLayout />;
  }

  if (workspaces.length === 0) {
    if (isAppleNativeApp) {
      return <Navigate to="/workspaces" replace />;
    }

    return <Navigate to="/workspaces/create" replace />;
  }

  return <Navigate to="/workspaces" replace />;
}

function App() {
  const navigate = useNavigate();
  const isNativePlatform = isNativePlatformRuntime();
  const isAppleNativeApp = isAppleNativeRuntime();

  // Listen for messages from service worker (notification clicks)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        console.log('[App] Received notification click message:', event.data.url);
        // Extract the path and query from the URL
        try {
          const url = new URL(event.data.url);
          const path = url.pathname + url.search;
          console.log('[App] Navigating to:', path);
          navigate(path);
        } catch (error) {
          console.error('[App] Failed to parse notification URL:', error);
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [navigate]);

  return (
    <>
      <Toaster />
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center">Loading...</div>}>
        {isNativePlatform ? <NativePlatformRuntime /> : null}
        <ImageLightbox />
        <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Login />} />
                  <Route path="/auth/callback" element={<KindeCallback />} />
                  <Route
                    path="/share"
                    element={
                      <ProtectedRoute>
                        <ShareReceiverPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/invite/:token" element={<InvitationAcceptPage />} />
                  <Route path="/join/:workspaceId" element={<WorkspaceJoinPage />} />
                  <Route
                    path="/workspaces"
                    element={
                      <ProtectedRoute>
                        <WorkspacePicker />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/workspaces/create"
                    element={
                      <ProtectedRoute>
                        {isAppleNativeApp ? <Navigate to="/workspaces" replace /> : <WorkspaceCreate />}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <HomeRoute />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <AdminRoute>
                        <AdminDashboard />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/workspace/settings"
                    element={
                      <WorkspaceAdminRoute>
                        <WorkspaceAdminDashboard />
                      </WorkspaceAdminRoute>
                    }
                  />
                  <Route
                    path="/workspace/:workspaceId/settings/billing/success"
                    element={
                      <ProtectedRoute>
                        <CheckoutSuccess />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/workspace/settings/billing/success"
                    element={
                      <ProtectedRoute>
                        <CheckoutSuccess />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
      </Suspense>
    </>
  )
}

function AppWrapper() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <WorkspaceProvider>
          <MindfulnessProvider>
            <TaskProvider>
              <MobileSidebarProvider>
                <App />
              </MobileSidebarProvider>
            </TaskProvider>
          </MindfulnessProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </Router>
  )
}

export default AppWrapper
